import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Progress } from "./ui/progress";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { compressImage, dataUrlSizeKb } from "../lib/image";
import {
  ChevronLeft,
  ChevronRight,
  Trees,
  Shield,
  Link2,
  Crown,
  Hammer,
  Layers,
  Clock4,
  CalendarDays,
  Calendar,
  CalendarClock,
  Check,
  CheckCircle2,
  X,
  Camera,
  ImagePlus,
} from "lucide-react";

const FENCE_TYPES = [
  { id: "timber", label: "Timber Paling", desc: "Classic backyard fencing", icon: Trees },
  { id: "colorbond", label: "Colorbond Steel", desc: "Low-maintenance & strong", icon: Shield },
  { id: "chain_link", label: "Chain Link", desc: "Security & commercial", icon: Link2 },
  { id: "aluminium", label: "Aluminium", desc: "Pool / decorative", icon: Layers },
  { id: "wrought_iron", label: "Wrought Iron", desc: "Heritage / ornamental", icon: Crown },
  { id: "other", label: "Other / Not Sure", desc: "We'll figure it out", icon: Hammer },
];

const TIMELINES = [
  { id: "asap", label: "ASAP", desc: "Within 2 weeks", icon: Clock4 },
  { id: "1_month", label: "Within 1 Month", desc: "Booking soon", icon: CalendarDays },
  { id: "1_3_months", label: "1–3 Months", desc: "Planning ahead", icon: Calendar },
  { id: "flexible", label: "Flexible", desc: "No rush", icon: CalendarClock },
];

const LENGTHS = [
  { id: 10, label: "0–20m", value: 15 },
  { id: 50, label: "20–50m", value: 35 },
  { id: 100, label: "50–100m", value: 75 },
  { id: 200, label: "100m+", value: 150 },
];

export default function QuoteWizard({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    fence_type: "",
    length_meters: 0,
    timeline: "",
    name: "",
    email: "",
    phone: "",
    suburb: "",
    message: "",
    photos: [],
  });
  const [photoBusy, setPhotoBusy] = useState(false);

  const MAX_PHOTOS = 5;

  const steps = ["Fence Type", "Length", "Timeline", "Your Details"];
  const progress = ((step + 1) / steps.length) * 100;

  const reset = () => {
    setStep(0);
    setDone(false);
    setForm({
      fence_type: "",
      length_meters: 0,
      timeline: "",
      name: "",
      email: "",
      phone: "",
      suburb: "",
      message: "",
      photos: [],
    });
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(reset, 400);
  };

  const canNext = () => {
    if (step === 0) return !!form.fence_type;
    if (step === 1) return form.length_meters > 0;
    if (step === 2) return !!form.timeline;
    if (step === 3) return form.name && form.email && form.phone && form.suburb;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Quote request sent! We'll be in touch within 24 hours.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent
        data-testid="quote-wizard-dialog"
        className="max-w-3xl w-[95vw] bg-[#0D0D0D] border border-[#333] rounded-none p-0 overflow-hidden text-white"
      >
        {/* Header */}
        <div className="border-b border-[#333] p-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF5A00] font-bold">
              {done ? "Sent" : `Step ${step + 1} / ${steps.length}`}
            </div>
            <DialogTitle className="font-heading text-3xl uppercase tracking-tight mt-1 text-white">
              {done ? "Cheers, mate." : steps[step]}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Get a free fencing quote from TradesmanDan in four quick steps.
            </DialogDescription>
          </div>
          <button
            data-testid="wizard-close"
            onClick={close}
            className="text-white/60 hover:text-white"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {!done && (
          <div className="h-1 bg-[#1A1A1A]">
            <div
              className="h-full bg-[#FF5A00] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        <div className="p-6 lg:p-8 max-h-[70vh] overflow-y-auto">
          {done ? (
            <div className="py-12 text-center" data-testid="wizard-success">
              <div className="w-20 h-20 bg-[#FF5A00] mx-auto flex items-center justify-center mb-8">
                <CheckCircle2 className="w-12 h-12 text-black" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-4xl uppercase tracking-tight mb-4">
                Your Quote Is On The Way
              </h3>
              <p className="text-[#A3A3A3] mb-8 max-w-md mx-auto">
                We've got your details. Dan or someone from the crew will be in
                touch within 24 business hours to book the on-site measure.
              </p>
              <button
                data-testid="wizard-done"
                onClick={close}
                className="bg-[#FF5A00] text-black font-heading tracking-[0.2em] uppercase px-8 py-4 hover:bg-[#E65100] transition-colors"
              >
                Back to Site
              </button>
            </div>
          ) : (
            <>
              {step === 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="wizard-step-fence">
                  {FENCE_TYPES.map((f) => (
                    <button
                      key={f.id}
                      data-testid={`fence-option-${f.id}`}
                      onClick={() => setForm({ ...form, fence_type: f.id })}
                      className={`text-left p-5 border transition-all ${
                        form.fence_type === f.id
                          ? "border-[#FF5A00] bg-[#FF5A00]/10"
                          : "border-[#333] hover:border-[#FF5A00]/50"
                      }`}
                    >
                      <f.icon
                        className={`w-7 h-7 mb-4 ${
                          form.fence_type === f.id ? "text-[#FF5A00]" : "text-white/60"
                        }`}
                        strokeWidth={1.5}
                      />
                      <div className="font-heading text-lg uppercase tracking-tight">
                        {f.label}
                      </div>
                      <div className="text-xs text-[#A3A3A3] mt-1">{f.desc}</div>
                    </button>
                  ))}
                </div>
              )}

              {step === 1 && (
                <div className="grid grid-cols-2 gap-3" data-testid="wizard-step-length">
                  {LENGTHS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`length-option-${l.id}`}
                      onClick={() => setForm({ ...form, length_meters: l.value })}
                      className={`p-6 border transition-all text-left ${
                        form.length_meters === l.value
                          ? "border-[#FF5A00] bg-[#FF5A00]/10"
                          : "border-[#333] hover:border-[#FF5A00]/50"
                      }`}
                    >
                      <div className="font-heading text-4xl uppercase tracking-tight">
                        {l.label}
                      </div>
                      <div className="text-xs tracking-[0.2em] uppercase text-[#A3A3A3] mt-2">
                        Approximate run
                      </div>
                    </button>
                  ))}
                  <div className="col-span-2 mt-2">
                    <Label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                      Or enter exact metres
                    </Label>
                    <Input
                      type="number"
                      data-testid="length-input-exact"
                      value={form.length_meters || ""}
                      onChange={(e) =>
                        setForm({ ...form, length_meters: Number(e.target.value) })
                      }
                      placeholder="e.g. 42"
                      className="mt-2 bg-[#1A1A1A] border-[#333] rounded-none text-white placeholder:text-[#666] focus:border-[#FF5A00]"
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="grid grid-cols-2 gap-3" data-testid="wizard-step-timeline">
                  {TIMELINES.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`timeline-option-${t.id}`}
                      onClick={() => setForm({ ...form, timeline: t.id })}
                      className={`p-5 border transition-all text-left ${
                        form.timeline === t.id
                          ? "border-[#FF5A00] bg-[#FF5A00]/10"
                          : "border-[#333] hover:border-[#FF5A00]/50"
                      }`}
                    >
                      <t.icon
                        className={`w-7 h-7 mb-4 ${
                          form.timeline === t.id ? "text-[#FF5A00]" : "text-white/60"
                        }`}
                        strokeWidth={1.5}
                      />
                      <div className="font-heading text-xl uppercase tracking-tight">
                        {t.label}
                      </div>
                      <div className="text-xs text-[#A3A3A3] mt-1">{t.desc}</div>
                    </button>
                  ))}
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4" data-testid="wizard-step-contact">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Full Name</Label>
                      <Input
                        data-testid="lead-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="mt-2 bg-[#1A1A1A] border-[#333] rounded-none text-white focus:border-[#FF5A00]"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Suburb</Label>
                      <Input
                        data-testid="lead-suburb"
                        value={form.suburb}
                        onChange={(e) => setForm({ ...form, suburb: e.target.value })}
                        className="mt-2 bg-[#1A1A1A] border-[#333] rounded-none text-white focus:border-[#FF5A00]"
                        placeholder="e.g. Pakenham"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Email</Label>
                      <Input
                        data-testid="lead-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="mt-2 bg-[#1A1A1A] border-[#333] rounded-none text-white focus:border-[#FF5A00]"
                        placeholder="you@example.com"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Phone</Label>
                      <Input
                        data-testid="lead-phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="mt-2 bg-[#1A1A1A] border-[#333] rounded-none text-white focus:border-[#FF5A00]"
                        placeholder="04XX XXX XXX"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Anything we should know? (optional)</Label>
                    <Textarea
                      data-testid="lead-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={3}
                      className="mt-2 bg-[#1A1A1A] border-[#333] rounded-none text-white focus:border-[#FF5A00]"
                      placeholder="Gates, slopes, access, etc."
                    />
                  </div>

                  {/* Photo upload (optional) */}
                  <div data-testid="wizard-photos">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                        Photos (optional · up to {MAX_PHOTOS})
                      </Label>
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#666]">
                        {form.photos.length} / {MAX_PHOTOS}
                      </span>
                    </div>
                    <p className="text-xs text-[#666] mb-3 leading-relaxed">
                      Snap a pic of the existing fence, site, or boundary — most jobs can be quoted from a photo alone, saving you a site visit.
                    </p>

                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {form.photos.map((p, i) => (
                        <div
                          key={i}
                          data-testid={`photo-thumb-${i}`}
                          className="relative aspect-square bg-[#1A1A1A] border border-[#333] group"
                        >
                          <img src={p} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            data-testid={`photo-remove-${i}`}
                            onClick={() =>
                              setForm({ ...form, photos: form.photos.filter((_, idx) => idx !== i) })
                            }
                            className="absolute top-1 right-1 w-6 h-6 bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FF5A00] hover:text-black"
                            aria-label="Remove photo"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                          <div className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] tracking-wider text-white/80 text-center py-0.5">
                            {dataUrlSizeKb(p)} KB
                          </div>
                        </div>
                      ))}

                      {form.photos.length < MAX_PHOTOS && (
                        <label
                          data-testid="photo-upload-trigger"
                          className={`aspect-square border-2 border-dashed flex flex-col items-center justify-center gap-1 cursor-pointer transition-all ${
                            photoBusy
                              ? "border-[#FF5A00]/50 bg-[#FF5A00]/5 cursor-wait"
                              : "border-[#333] hover:border-[#FF5A00] hover:bg-[#FF5A00]/5 text-white/60 hover:text-[#FF5A00]"
                          }`}
                        >
                          {photoBusy ? (
                            <>
                              <ImagePlus className="w-6 h-6 text-[#FF5A00] animate-pulse" />
                              <span className="text-[9px] tracking-[0.2em] uppercase text-[#FF5A00]">
                                Compressing…
                              </span>
                            </>
                          ) : (
                            <>
                              <Camera className="w-6 h-6" />
                              <span className="text-[9px] tracking-[0.2em] uppercase">Add</span>
                            </>
                          )}
                          <input
                            data-testid="photo-input"
                            type="file"
                            accept="image/*"
                            multiple
                            capture="environment"
                            disabled={photoBusy}
                            className="hidden"
                            onChange={async (e) => {
                              const files = Array.from(e.target.files || []);
                              e.target.value = ""; // allow re-selecting same file
                              if (files.length === 0) return;
                              setPhotoBusy(true);
                              try {
                                const slots = MAX_PHOTOS - form.photos.length;
                                const toProcess = files.slice(0, slots);
                                const compressed = [];
                                for (const f of toProcess) {
                                  try {
                                    const url = await compressImage(f);
                                    compressed.push(url);
                                  } catch (err) {
                                    toast.error(`Couldn't read ${f.name}`);
                                  }
                                }
                                if (compressed.length) {
                                  setForm((prev) => ({
                                    ...prev,
                                    photos: [...prev.photos, ...compressed].slice(0, MAX_PHOTOS),
                                  }));
                                  if (files.length > slots) {
                                    toast.info(`Added ${compressed.length} of ${files.length} — limit is ${MAX_PHOTOS}`);
                                  }
                                }
                              } finally {
                                setPhotoBusy(false);
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {!done && (
          <div className="border-t border-[#333] p-6 flex items-center justify-between gap-4">
            <button
              data-testid="wizard-back"
              disabled={step === 0}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="flex items-center gap-2 px-4 py-3 text-sm tracking-[0.2em] uppercase font-bold text-white/70 hover:text-white disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            {step < steps.length - 1 ? (
              <button
                data-testid="wizard-next"
                disabled={!canNext()}
                onClick={() => setStep((s) => s + 1)}
                className="flex items-center gap-2 bg-[#FF5A00] text-black font-heading tracking-[0.2em] uppercase px-6 py-3 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#E65100] transition-colors"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                data-testid="wizard-submit"
                disabled={!canNext() || submitting}
                onClick={submit}
                className="flex items-center gap-2 bg-[#FF5A00] text-black font-heading tracking-[0.2em] uppercase px-6 py-3 disabled:opacity-50 hover:bg-[#E65100] transition-colors"
              >
                {submitting ? "Sending…" : "Send Quote Request"}
                <Check className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
